import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {Observable, of, Subject} from 'rxjs';
import {filter, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {Opportunity} from '../../interfaces/opportunity.interface';
import {OpportunityService} from '../../services/opportunity.service';
import {FollowUp} from '../../interfaces/follow-up.interface';
import {OpportunityStatuses} from '../../interfaces/opportunity-statuses.enum';
import {FollowUpsListComponent} from '../follow-ups-list/follow-ups-list.component';
import {DisplayValueComponent} from '../display-value/display-value.component';
import {OpportunityContextualMenuComponent} from '../opportunity-contextual-menu/opportunity-contextual-menu.component';
import {MatProgressBar} from '@angular/material/progress-bar';
import {MatIcon} from '@angular/material/icon';
import {MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle} from '@angular/material/card';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatMenu, MatMenuTrigger} from '@angular/material/menu';
import {AsyncPipe} from '@angular/common';
import {MatIconButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatTooltip} from '@angular/material/tooltip';
import {OpportunityChangeCompanyDialogComponent} from '../opportunity-change-company-dialog/opportunity-change-company-dialog.component';
import {Company} from '../../interfaces/company.interface';



@Component({
    selector: 'app-opportunity-details',
    templateUrl: './opportunity-details.component.html',
    imports: [
        FollowUpsListComponent,
        DisplayValueComponent,
        OpportunityContextualMenuComponent,
        MatProgressBar,
        MatIcon,
        MatCard,
        MatProgressSpinner,
        MatMenu,
        AsyncPipe,
        RouterLink,
        MatMenuTrigger,
        MatCardTitle,
        MatIconButton,
        MatCardHeader,
        MatCardSubtitle,
        MatCardContent,
        MatTooltip
    ],
    styleUrls: ['./opportunity-details.component.scss']
})
export class OpportunityDetailsComponent implements OnDestroy {

    private unsubscribe$: Subject<void> = new Subject<void>();
    opportunity$: Observable<Opportunity>;

    selectedFollowUp: FollowUp | null;

    company$: Observable<any>;

    readonly OpportunityStatuses = OpportunityStatuses;

    constructor(
        private activatedRoute: ActivatedRoute,
        private opportunityService: OpportunityService,
        private router: Router,
        private matDialog: MatDialog,
        private snackBar: MatSnackBar
    ) {
        this.opportunity$ = this.activatedRoute.paramMap.pipe(
            takeUntil(this.unsubscribe$),
            filter(params => params.has('id')),
            map(params => params.get('id')),
            switchMap(oId => this.opportunityService.getOpportunityByOpportunityId$(oId)),
            tap(o => {
                if (!o) {
                    this.router.navigateByUrl('/opportunities');
                } else if (o.companyId) {
                    this.company$ = this.opportunityService.getCompanyDetails$(o.companyId);
                } else {
                    this.company$ = of(null);
                }
            })
        );
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    onFollowUpSelection(fu: FollowUp) {
        this.selectedFollowUp = fu;
    }

    openChangeCompanyDialog(opportunity: Opportunity) {
        const dialogRef = this.matDialog.open(OpportunityChangeCompanyDialogComponent, {
            width: '450px',
            data: opportunity
        });

        dialogRef.afterClosed().pipe(
            take(1),
            filter((company: Company | null) => !!company)
        ).subscribe(async (company: Company) => {
            try {
                await this.opportunityService.changeOpportunityCompany(opportunity, company);
                if (company.id) {
                    this.company$ = this.opportunityService.getCompanyDetails$(Number(company.id));
                }
                this.snackBar.open('Opportunity client updated successfully!', 'OK', {duration: 3000});
            } catch (err: any) {
                console.error('Error changing opportunity client:', err);
                this.snackBar.open(`Error updating client: ${err?.message || err}`, 'OK', {duration: 4000});
            }
        });
    }
}
